import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useDataContext } from '../contexts/DataContext';
import { formatDateForDateTimeLocal, formatDateTimeLocalToISO } from '../utils/dateUtils';

const VitalSignsChart = ({ vitalSigns, patientId, onUpdate }) => {
  const { addVitals, updateVitals, deleteVitals } = useDataContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVital, setEditingVital] = useState(null);
  const [formData, setFormData] = useState({
    measurement_date: '',
    systolic_pressure: '',
    diastolic_pressure: '',
    heart_rate: '',
    weight: '',
    height: '',
    temperature: '',
    blood_glucose: '',
    notes: ''
  });

  const handleOpenDialog = (vital = null) => {
    if (vital) {
      setEditingVital(vital);
      setFormData({
        measurement_date: formatDateForDateTimeLocal(vital.measurement_date),
        systolic_pressure: vital.systolic_pressure || '',
        diastolic_pressure: vital.diastolic_pressure || '',
        heart_rate: vital.heart_rate || '',
        weight: vital.weight || '',
        height: vital.height || '',
        temperature: vital.temperature || '',
        blood_glucose: vital.blood_glucose || '',
        notes: vital.notes || ''
      });
    } else {
      setEditingVital(null);
      setFormData({
        measurement_date: formatDateForDateTimeLocal(new Date().toISOString()),
        systolic_pressure: '',
        diastolic_pressure: '',
        heart_rate: '',
        weight: '',
        height: '',
        temperature: '',
        blood_glucose: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVital(null);
    setFormData({
      measurement_date: '',
      systolic_pressure: '',
      diastolic_pressure: '',
      heart_rate: '',
      weight: '',
      height: '',
      temperature: '',
      blood_glucose: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      // Convertir les valeurs string en nombres pour les champs numériques
      const vitalData = {
        patientId: patientId,
        measurement_date: formatDateTimeLocalToISO(formData.measurement_date),
        systolic_pressure: formData.systolic_pressure ? parseInt(formData.systolic_pressure) : undefined,
        diastolic_pressure: formData.diastolic_pressure ? parseInt(formData.diastolic_pressure) : undefined,
        heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
        blood_glucose: formData.blood_glucose ? parseFloat(formData.blood_glucose) : undefined,
        notes: formData.notes || undefined
      };

      if (editingVital) {
        await updateVitals(editingVital.id, vitalData);
      } else {
        await addVitals(vitalData);
      }

      handleCloseDialog();
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Afficher les erreurs de validation si elles existent
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map(err => err.msg).join('\n');
        alert(`Erreurs de validation:\n${errorMessages}`);
      } else {
        alert('Erreur lors de la sauvegarde. Veuillez vérifier vos données.');
      }
    }
  };

  const handleDelete = async (vitalId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette mesure ?')) {
      try {
        await deleteVitals(vitalId);
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Préparer les données pour le graphique
  const chartData = vitalSigns?.map(vital => ({
    date: formatDate(vital.measurement_date),
    systolic: vital.systolic_pressure,
    diastolic: vital.diastolic_pressure,
    heartRate: vital.heart_rate,
    weight: vital.weight,
    bmi: vital.bmi
  })) || [];

  // Calculer les moyennes
  const averages = vitalSigns?.length > 0 ? {
    avgSystolic: Math.round(vitalSigns.reduce((sum, v) => sum + (v.systolic_pressure || 0), 0) / vitalSigns.length),
    avgDiastolic: Math.round(vitalSigns.reduce((sum, v) => sum + (v.diastolic_pressure || 0), 0) / vitalSigns.length),
    avgHeartRate: Math.round(vitalSigns.reduce((sum, v) => sum + (v.heart_rate || 0), 0) / vitalSigns.length),
    avgWeight: (vitalSigns.reduce((sum, v) => sum + (v.weight || 0), 0) / vitalSigns.length).toFixed(1)
  } : {};

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Constantes Vitales</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouvelle Mesure
        </Button>
      </Box>

      {/* Moyennes */}
      {Object.keys(averages).length > 0 && (
        <Box display="flex" gap={2} mb={3}>
          <Chip label={`Tension moyenne: ${averages.avgSystolic}/${averages.avgDiastolic} mmHg`} color="primary" />
          <Chip label={`FC moyenne: ${averages.avgHeartRate} bpm`} color="secondary" />
          <Chip label={`Poids moyen: ${averages.avgWeight} kg`} color="default" />
        </Box>
      )}

      {/* Graphique */}
      {chartData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Évolution de la Tension Artérielle
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="systolic" stroke="#8884d8" name="Systolique" />
                <Line type="monotone" dataKey="diastolic" stroke="#82ca9d" name="Diastolique" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Liste des mesures */}
      {vitalSigns && vitalSigns.length > 0 ? (
        vitalSigns
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((vital) => (
          <Card key={vital.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Typography variant="h6">
                  {formatDate(vital.measurement_date)}
                </Typography>
                <Box>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleOpenDialog(vital)}
                  >
                    Modifier
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleDelete(vital.id)}
                  >
                    Supprimer
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {vital.systolic_pressure && vital.diastolic_pressure && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="primary">
                      Tension Artérielle
                    </Typography>
                    <Typography variant="h6">
                      {vital.systolic_pressure}/{vital.diastolic_pressure} mmHg
                    </Typography>
                  </Grid>
                )}

                {vital.heart_rate && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" color="secondary">
                      Fréquence Cardiaque
                    </Typography>
                    <Typography variant="h6">
                      {vital.heart_rate} bpm
                    </Typography>
                  </Grid>
                )}

                {vital.weight && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2">
                      Poids
                    </Typography>
                    <Typography variant="h6">
                      {vital.weight} kg
                    </Typography>
                  </Grid>
                )}

                {vital.bmi && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2">
                      IMC
                    </Typography>
                    <Typography variant="h6">
                      {vital.bmi}
                    </Typography>
                  </Grid>
                )}

                {vital.temperature && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2">
                      Température
                    </Typography>
                    <Typography variant="h6">
                      {vital.temperature}°C
                    </Typography>
                  </Grid>
                )}

                {vital.blood_glucose && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2">
                      Glycémie
                    </Typography>
                    <Typography variant="h6">
                      {vital.blood_glucose} mg/dL
                    </Typography>
                  </Grid>
                )}

                {vital.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      Notes:
                    </Typography>
                    <Typography variant="body2">
                      {vital.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography variant="body1" color="textSecondary">
          Aucune mesure enregistrée
        </Typography>
      )}

      {/* Dialog pour ajouter/modifier une mesure */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVital ? 'Modifier la mesure' : 'Nouvelle mesure'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de mesure"
                type="datetime-local"
                value={formData.measurement_date}
                onChange={(e) => setFormData({ ...formData, measurement_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Systolique (mmHg)"
                type="number"
                value={formData.systolic_pressure}
                onChange={(e) => setFormData({ ...formData, systolic_pressure: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Diastolique (mmHg)"
                type="number"
                value={formData.diastolic_pressure}
                onChange={(e) => setFormData({ ...formData, diastolic_pressure: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Fréquence cardiaque (bpm)"
                type="number"
                value={formData.heart_rate}
                onChange={(e) => setFormData({ ...formData, heart_rate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Poids (kg)"
                type="number"
                step="0.1"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Taille (cm)"
                type="number"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Température (°C)"
                type="number"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Glycémie (mg/dL)"
                type="number"
                value={formData.blood_glucose}
                onChange={(e) => setFormData({ ...formData, blood_glucose: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingVital ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VitalSignsChart;
